import { IImportable } from 'library/ts/interfaces/importable/IImportable';
import { AbstractAdSetupFactory } from 'tag/ts/tag/factories/ads/AbstractAdSetupFactory';
import { IAdSetupInfo } from 'tag/ts/tag/interfaces/ads/IAdSetupInfo';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { ITagConfig } from 'tag/ts/tag/interfaces/config/ITagConfig';
import { AdSDK } from 'tag/ts/tag/model/type/ads/AdSDK';
import { AdSetupDefinition } from 'tag/ts/tag/model/type/ads/AdSetupDefinition';
import { AdSetupType } from 'tag/ts/tag/model/type/ads/AdSetupType';

/**
 * @copyright © Paramount Streaming
 * @author  Zeeshan Ahmad
 * @description Factory class used to create instances of IAdSetupInfo for playback on desktop
 */
export class DesktopAdSetupFactory extends AbstractAdSetupFactory {

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				name: 'DesktopAdSetupFactory',
				...argument
			}
		);
	}

	/**
	 * @description Returns a complex object representing ad setup for playback on desktop
	 * @returns IAdSetupInfo
	 */
	createAdSetupInfo(): IAdSetupInfo {
		// sanity check
		if ( this.utils.Type.isEmpty( this.tag ) ) {
			this?.logger?.warn( 'createAdSetupInfo', 'tag is empty, cannot create adSetupInfo... exiting' );
			return undefined;
		}

		const tagConfig: ITagConfig = this.tag?.model?.config?.tag;
		const contentUtilities = this.tag?.model?.contentUtilities;
		const tagUtilities = this.tag?.tagUtilities;
		const params = this.tag?.params;
		const apiMetadata = this.tag?.model?.apiMetadata;
		const isLive: boolean = contentUtilities.isLive( params, apiMetadata );
		const isPID: boolean = contentUtilities.isPID( this.tag );
		const isLiveDAIStream: boolean = contentUtilities.isLiveDAIStream( apiMetadata );
		const hasDAIAssetKey: boolean = contentUtilities.hasDAIAssetKey( params, apiMetadata );
		const isAssetURL: boolean = contentUtilities.isAssetURL( this.tag );
		const isInternational: boolean = tagUtilities.isInternationalContext( this.tag ) === true;
		const videoAdsDisabled: boolean = params?.AD?.VIDEOADS?.enabled === false;
		const isLinearAdTrackable: boolean = contentUtilities.isLinearAdTrackable();
		const linearAdsRequestTemplate = this.tag?.model?.adUtilities?.getLinearAdTrackUrl?.();
		const hasLinearAdsRequestTemplate = (
			this.utils.Type.isString( linearAdsRequestTemplate )
			&&
			!this.utils.Type.isEmpty( linearAdsRequestTemplate )
		);
		const isClid3Eligible: boolean = isLinearAdTrackable && hasLinearAdsRequestTemplate;

		const adSetupInfo = this.createDefaultAdSetupInfo();
		this?.logger?.log( 'createAdSetupInfo', 'created default adSetupInfo object requiring no ads:', adSetupInfo );

		// IMPORTANT: CLID3-only (linear ad tracking) => definition: LINEAR_AD_TRACKING
		// - CLID3 + DAI must keep the DAI setup.
		if ( isClid3Eligible && isLive && isPID && !isLiveDAIStream ) {
			adSetupInfo.setup.definition = AdSetupDefinition.LINEAR_AD_TRACKING;
			this?.logger?.log( 'createAdSetupInfo', 'is linear ad trackable... updated setup definition:', adSetupInfo );
			return adSetupInfo;
		}

		// USE CASE: VIDEOADS in paramsVO is disabled => No Ad SDK and Ad setup required for ALL users
		// added for DAI SDK for INTL ad flow
		// https://paramount.atlassian.net/browse/CBSVIDEO-6725
		// https://paramount.atlassian.net/browse/CBSVIDEO-6726
		if ( isInternational && videoAdsDisabled ) {
			// expecting INTL standard-tier users to have VIDEOADS disabled
			this?.logger?.log( 'createAdSetupInfo', 'is INTL context, VIDEOADS is disabled... no ad setup required:', adSetupInfo );
			return adSetupInfo;
		}

		// USE CASE: Stitcher SSAI => FAST (Free Ad-Supported Streaming) for Pluto TV. No SDK required, ads managed by Blackbird avia-js plugin.
		if ( this.hasStitcherParams() ) {
			adSetupInfo.setup.type = AdSetupType.SSAI;
			adSetupInfo.setup.definition = AdSetupDefinition.STITCHER;
			this?.logger?.log( 'createAdSetupInfo', 'stitcher params detected... setup ad playback via SSAI with Stitcher, no SDK required:', adSetupInfo );
			return adSetupInfo;
		}

		// USE CASE: Syncbak Live Local station, playback by assetURL => No Ad SDK and Ad setup required for ALL users
		if ( isAssetURL && isLive ) {
			this?.logger?.log( 'createAdSetupInfo', 'asset is livestream, playback via assetUrl... no ad setup required:', adSetupInfo );
			return adSetupInfo;
		}

		// USE CASE: Live DAI stream played by PID
		if ( isLive && isPID && hasDAIAssetKey && isLiveDAIStream ) {
			adSetupInfo.setup.type = AdSetupType.SSAI; // Server Side Ad Insertion
			adSetupInfo.setup.definition = AdSetupDefinition.DAI_SDK; // DAI SDK
			adSetupInfo.sdk.type = AdSDK.IMA_DAI; // set ad SDK config module name
			adSetupInfo.sdk.module = tagConfig?.Imports?.params?.ad_sdk[ adSetupInfo.sdk.type ] as IImportable; // set ad SDK Import module from tag config
			this?.logger?.log( 'createAdSetupInfo', 'asset is DAI livestream, playback via PID... setup ad playback via SSAI with DAI SDK:', adSetupInfo );
			return adSetupInfo;
		}

		// USE CASE: Live DAI SSB stream played by PID
		if ( isLive && isPID && !isLiveDAIStream ) {
			adSetupInfo.setup.type = AdSetupType.SSAI; // Server Side Ad Insertion
			adSetupInfo.setup.definition = AdSetupDefinition.DAI_SSB; // DAI SSB
			adSetupInfo.sdk.type = AdSDK.NONE; // set ad SDK config module name
			adSetupInfo.sdk.module = undefined; // set ad SDK Import module from tag config
			this?.logger?.log( 'createAdSetupInfo', 'asset is DAI SSB livestream, playback via PID... setup ad playback via SSAI with DAI SSB, resolving:', adSetupInfo );
			return adSetupInfo;
		}

		// USE CASE: VOD played by DAI SDK
		if ( !isLive && isPID ) {
			adSetupInfo.setup.type = AdSetupType.SSAI;
			adSetupInfo.setup.definition = AdSetupDefinition.DAI_SDK;
			adSetupInfo.sdk.type = AdSDK.IMA_DAI;
			adSetupInfo.sdk.module = tagConfig?.Imports?.params?.ad_sdk[ adSetupInfo.sdk.type ] as IImportable;
			this?.logger?.log( 'createAdSetupInfo', 'asset is VOD, playback via PID... setup ad playback via SSAI with DAI SDK, resolving:', adSetupInfo );
			return adSetupInfo;
		}

		this?.logger?.log( 'createAdSetupInfo', 'no matching use case, returning default adSetupInfo:', adSetupInfo );
		return adSetupInfo;
	}
}